import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MarketLink as Link } from './MarketLink';
import { BadgeCheck, Heart, Star, Truck } from 'lucide-react';
import { getTranslation as t, isRTL } from '../utils/translations';
import Image from './Image';
import Hls from 'hls.js';
import { getProduct, LuxWebCache } from '../api/client';
import { usePlugins } from '../context/PluginContext';
import { DEFAULT_STOREFRONT_SETTINGS, normalizeStorefrontSettings } from '../utils/storefrontSettings';

const getIconEmoji = (iconName) => {
    switch(iconName) {
        case 'Flame': return '🔥';
        case 'Zap': return '⚡';
        case 'Sparkles': return '✨';
        case 'Gift': return '🎁';
        case 'Percent': return '🏷️';
        case 'Star': return '⭐';
        case 'Heart': return '❤️';
        case 'Clock': return '🕒';
        case 'TrendingUp': return '📈';
        case 'Award': return '🏆';
        default: return '';
    }
};

const ProductCard = ({ product, onAddToCart, onBuyNow, showToast, cardHoverStyle = 'scale', customBorderColor = 'glass', luxuryHighlights = false }) => {
    const { activePlugins, isPluginActive, getPluginConfig, pluginsLoaded } = usePlugins();
    const storeSettings = useMemo(() => {
        if (!pluginsLoaded || !activePlugins) {
            return DEFAULT_STOREFRONT_SETTINGS;
        }
        if (!isPluginActive('advanced_seo')) {
            return DEFAULT_STOREFRONT_SETTINGS;
        }
        const pluginConfig = getPluginConfig('advanced_seo') || {};
        return normalizeStorefrontSettings(pluginConfig.storefrontSettings || {});
    }, [activePlugins, getPluginConfig, isPluginActive, pluginsLoaded]);

    const [visible, setVisible] = useState(false);
    const [liked, setLiked] = useState(false);
    const [activeImgIndex, setActiveImgIndex] = useState(0);
    const ref = useRef();
    const touchStartX = useRef(null);
    const longPressTimer = useRef(null);

    const hoverClass = cardHoverStyle === 'scale' 
        ? 'hover-scale' 
        : cardHoverStyle === 'glow' 
            ? 'hover-glow' 
            : cardHoverStyle === 'zoom-in' 
                ? 'hover-zoom-in' 
                : '';
                
    const borderClass = customBorderColor === 'gold' 
        ? 'border-gold' 
        : customBorderColor === 'purple' 
            ? 'border-purple' 
            : customBorderColor === 'transparent' 
                ? 'border-transparent' 
                : 'border-glass';
                
    const highlightClass = luxuryHighlights ? 'luxury-shimmer-sweep' : '';

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
        }, { rootMargin: '600px' });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    const displayName = (isRTL() ? (product.nameAr || product.name) : (product.nameEn || product.name)) || product.name || 'Product';
    const displayCategory = product.categoryName || product.category || '';
    const displayPrice = Number(product.salePrice || product.price || 0);
    const comparePrice = Number(product.compareAtPrice || product.comparePrice || product.oldPrice || product.originalPrice || 0);
    const rawDiscount = product.discountPercentage || product.discountPercent || product.discount;
    const discountPercent = comparePrice > displayPrice && displayPrice > 0
        ? Math.round(((comparePrice - displayPrice) / comparePrice) * 100)
        : (rawDiscount ? Number(rawDiscount) : null);
    const verified = product.verified === true || product.isVerified === true || product.brandVerified === true;
    const rating = Number(product.ratingAverage || product.rating || product.averageRating || 0);
    const reviewCount = Number(product.reviewCount || product.ratingCount || 0);
    const soldCount = Number(product.soldCount || product.salesCount || product.orderCount || 0);
    const stockCount = Number(product.stock || product.quantity || 0);
    const isBestSeller = soldCount >= 10;
    const isTrending = product.featured === true || product.isTrending || soldCount >= 5;
    const isLimitedStock = stockCount > 0 && stockCount <= 5;
    const isFreeShipping = product.freeShipping === true || displayPrice >= 50;

    const allImages = useMemo(() => {
        const list = [];
        const primary = product.primaryImageUrl || product.image;
        if (primary) list.push(primary);
        if (product.secondaryImageUrl && !list.includes(product.secondaryImageUrl)) list.push(product.secondaryImageUrl);
        if (Array.isArray(product.images)) {
            product.images.forEach(img => {
                const url = typeof img === 'string' ? img : img?.url || img?.src || img?.imageUrl;
                if (url && !list.includes(url)) list.push(url);
            });
        }
        if (Array.isArray(product.galleryImages)) {
            product.galleryImages.forEach(img => {
                const url = typeof img === 'string' ? img : img?.url || img?.src || img?.imageUrl;
                if (url && !list.includes(url)) list.push(url);
            });
        }
        return list.length > 0 ? list : ['/media/products/placeholder.png'];
    }, [product]);

    const displayBrand = product.brandName || product.brand || displayCategory;

    const colorValues = product.attributes
        ?.find(a => String(a?.name || '').toLowerCase().includes('color'))
        ?.detailedValues
        ?.filter(val => val?.hexCode)
        ?.slice(0, 5) || [];

    const currencyCode = (typeof product?.currencyCode === 'string' && product.currencyCode.trim().length === 3)
        ? product.currencyCode.trim().toUpperCase()
        : 'USD';
    const formatPrice = (price) => {
        try {
            return new Intl.NumberFormat(undefined, { 
                style: 'currency', currency: currencyCode, minimumFractionDigits: 0, maximumFractionDigits: 2 
            }).format(Number(price) || 0);
        } catch (_) {
            return `${Number(price || 0).toLocaleString()} ${currencyCode}`;
        }
    };

    const handleLike = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setLiked(!liked);
    };

    const renderStars = (count) => {
        const stars = [];
        const full = Math.floor(count);
        for (let i = 0; i < 5; i++) {
            stars.push(
                <Star 
                    key={i} 
                    size={11} 
                    fill={i < full ? '#f59e0b' : 'none'} 
                    stroke={i < full ? '#f59e0b' : 'rgba(255,255,255,0.2)'} 
                    strokeWidth={1.5}
                />
            );
        }
        return stars;
    };

    const cardVideoRef = useRef(null);
    const hasPrefetchedRef = useRef(false);

    const prefetchProductDetails = () => {
        if (hasPrefetchedRef.current || !product?.id) return;
        hasPrefetchedRef.current = true;
        LuxWebCache.preloadImages(allImages);
        getProduct(product.id).catch(() => {});
    };

    const handleMouseEnter = () => {
        prefetchProductDetails();
        if (cardVideoRef.current) {
            cardVideoRef.current.play().catch(() => {});
        }
    };

    const handleMouseLeave = () => {
        if (cardVideoRef.current) {
            cardVideoRef.current.pause();
        }
    };

    const handleTouchStart = (e) => {
        prefetchProductDetails();
        touchStartX.current = e.touches[0].clientX;
        longPressTimer.current = setTimeout(() => {
            if (onBuyNow) {
                if (showToast) showToast(t('PROCEEDING_TO_BUY') || 'Proceeding to buy...');
                onBuyNow(product);
            }
        }, 600);
        if (cardVideoRef.current) {
            if (cardVideoRef.current.paused) {
                cardVideoRef.current.play().catch(() => {});
            } else {
                cardVideoRef.current.pause();
            }
        }
    };

    const handleTouchMove = (e) => {
        if (touchStartX.current !== null && Math.abs(e.touches[0].clientX - touchStartX.current) > 10) {
            clearTimeout(longPressTimer.current);
        }
    };

    const handleTouchEnd = (e) => {
        clearTimeout(longPressTimer.current);
        if (touchStartX.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(diff) > 40 && allImages.length > 1) {
            e.preventDefault();
            e.stopPropagation();
            if (diff > 0) {
                setActiveImgIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
            } else {
                setActiveImgIndex((prev) => (prev + 1) % allImages.length);
            }
        }
        touchStartX.current = null;
    };

    return (
        <div 
            className={`product-card glass-panel ${hoverClass} ${borderClass} ${highlightClass}`} 
            ref={ref}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
        >
            <Link to={`/product/${product.slug || product.id}`} className="product-card-link" style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, height: '100%', textDecoration: 'none' }}>
                <div className="product-card-img-wrap" style={{ aspectRatio: '1/1', maxHeight: '220px', width: '100%', position: 'relative', overflow: 'hidden', background: '#f8fafc' }}>
                    {visible && product.videoUrl ? (
                        <>
                            <video
                                ref={el => {
                                    cardVideoRef.current = el;
                                    if (!el) return;
                                    const src = product.videoUrl;
                                    if (src.includes('.m3u8')) {
                                        if (Hls.isSupported()) {
                                            const hls = new Hls({ enableWorker: false });
                                            hls.loadSource(src);
                                            hls.attachMedia(el);
                                        } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
                                            el.src = src;
                                        }
                                    } else {
                                        el.src = src;
                                    }
                                }}
                                poster={allImages[activeImgIndex] || product.primaryImageUrl || product.image || ''}
                                muted loop playsInline
                                style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0, zIndex: 2 }}
                            />
                            <Image src={allImages[activeImgIndex]} alt={product.name} aspectRatio="1/1" lazy={false} style={{ visibility: 'hidden' }} />
                        </>
                    ) : (
                        <Image
                            src={allImages[activeImgIndex]}
                            alt={displayName}
                            aspectRatio="1/1"
                            lazy={true}
                        />
                    )}

                    {/* Image Swiping Dots */}
                    {allImages.length > 1 && (
                        <div className="product-card-img-indicators">
                            {allImages.slice(0, 5).map((_, idx) => (
                                <span
                                    key={idx}
                                    className={`card-img-dot ${idx === activeImgIndex ? 'active' : ''}`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Small Professional Corner Badge */}
                    <div className="lux-card-badges" style={{ position: 'absolute', top: '8px', left: isRTL() ? 'auto' : '8px', right: isRTL() ? '8px' : 'auto', zIndex: 10, display: 'flex', gap: '4px', maxWidth: 'calc(100% - 46px)', pointerEvents: 'none' }}>
                        {discountPercent ? (
                            <span className="lux-badge-square" style={{ background: '#FE2C55', color: '#ffffff', padding: '3px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: '800', letterSpacing: '0.3px', lineHeight: '1.2', boxShadow: '0 2px 6px rgba(254, 44, 85, 0.35)', whiteSpace: 'nowrap' }}>
                                {isRTL() ? `خصم ${discountPercent}%` : `-${discountPercent}%`}
                            </span>
                        ) : product.badgeText ? (
                            <span className="lux-badge-square" style={{ background: '#111111', color: '#ffffff', padding: '4px 6px', borderRadius: '4px', fontSize: '10.5px', fontWeight: '800', letterSpacing: '0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                {product.badgeText}
                            </span>
                        ) : isTrending ? (
                            <span className="lux-badge-square" style={{ background: '#111111', color: '#ffffff', padding: '4px 6px', borderRadius: '4px', fontSize: '10.5px', fontWeight: '800', letterSpacing: '0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                {isRTL() ? '★ رائج' : '★ HOT'}
                            </span>
                        ) : isBestSeller ? (
                            <span className="lux-badge-square" style={{ background: '#111111', color: '#ffffff', padding: '4px 6px', borderRadius: '4px', fontSize: '10.5px', fontWeight: '800', letterSpacing: '0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                ★ BEST
                            </span>
                        ) : null}
                    </div>

                    {/* Wishlist Button in Opposite Corner */}
                    <button 
                        className={`product-card-favorite ${liked ? 'liked' : ''}`}
                        onClick={handleLike}
                        aria-label="Add to wishlist"
                        style={{ position: 'absolute', top: '8px', right: isRTL() ? 'auto' : '8px', left: isRTL() ? '8px' : 'auto', zIndex: 10, width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}
                    >
                        <Heart size={15} strokeWidth={2.2} fill={liked ? '#FE2C55' : 'none'} color={liked ? '#FE2C55' : '#111111'} />
                    </button>
                </div>

                <div className="product-card-copy" style={{ 
                    padding: '10px 12px 12px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '4px',
                    textAlign: isRTL() ? 'right' : 'left',
                    direction: isRTL() ? 'rtl' : 'ltr'
                }}>
                    {/* 1. Product Name - Alibaba: plain, 2 lines, small */}
                    <h3 className="product-card-name" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#111827',
                        fontWeight: '500',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        margin: 0
                    }}>
                        {displayName}
                    </h3>

                    {/* 2. Price - Alibaba: bold vibrant red, large */}
                    <div className="product-card-pricing-alibaba" style={{ marginTop: '4px', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span className="product-card-price" style={{ color: '#FE2C55', fontSize: '18px', fontWeight: '800', letterSpacing: '-0.3px' }}>
                            {formatPrice(displayPrice)}
                        </span>
                        {discountPercent ? (
                            <span className="product-card-compare" style={{ color: '#9ca3af', textDecoration: 'line-through', fontSize: '12px', fontWeight: '400' }}>
                                {formatPrice(comparePrice || displayPrice * 1.25)}
                            </span>
                        ) : null}
                    </div>

                    {/* 3. MOQ + Sold - Alibaba style: plain small text */}
                    <div className="product-card-trade-badges" style={{ fontSize: '12px', color: '#666', lineHeight: '1.5', marginTop: '2px' }}>
                        <span style={{ fontWeight: '400' }}>
                            {isRTL() ? 'أقل كمية: 1 قطعة' : 'MOQ: 1 piece'}
                        </span>
                        {soldCount > 0 && (
                            <span style={{ marginInlineStart: '8px', color: '#666' }}>
                                {soldCount.toLocaleString()} {isRTL() ? 'مبيع' : 'sold'}
                            </span>
                        )}
                    </div>

                    {/* 4. Verified + Shipping - Alibaba style: small colored text */}
                    <div className="product-card-footer" style={{ fontSize: '12px', lineHeight: '1.5', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {verified && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', color: '#10B981', fontWeight: '700', fontSize: '12px' }}>
                                <BadgeCheck size={13} strokeWidth={2.5} />
                                {isRTL() ? 'مُعتمد' : 'Verified'}
                            </span>
                        )}
                        {isFreeShipping && storeSettings?.freeShippingWorldwideEnabled !== false && (
                            <span style={{ color: '#666', fontSize: '12px' }}>
                                {isRTL() ? '· شحن مجاني' : '· Free Shipping'}
                            </span>
                        )}
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default React.memo(ProductCard);
